import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { DataTable, DataTableStringComparison } from "../../../components/DataTable";
import { AdminPage } from "../AdminPage";
import { LocationEditor } from "./LocationEditor";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { SearchInput } from "../../../components/SearchInput";

export const LocationsPage = ({userData, canViewMachines, canAdd, canEdit, canDelete, canAssignMachines}) => {
    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [deleteId, setDeleteId] = useState<number|null>(null);
    const [search, setSearch] = useState('');
    const { t } = useTranslation();

    const fetch = useCallback(() => {
        setLoading(true);

        API.GET('/api/admlocations')
            .then(l => {
                setLocations(l); 
                setLoading(false);
        });
    }, []);

    useEffect(() => {
        fetch();
    }, []);

    const Table = useMemo(() => (
        <DataTable
            data={locations}
            onRowSelected={l => setSelectedId(l.id)}
            height={500}
            search={search}
            rowClassname={l => l.lType == 1 ? 'bg-warning bg-opacity-25' : (l.lType == 2 ? 'bg-info bg-opacity-25' : '')}
            columns={[
                { headerName: t('name'), flex: 3, valueGetter: ({data}) => ({Name: data.Name, lType: data.lType, address: data.address}),
                    cellRenderer: ({value}) => (
                        <span className="d-flex flex-row align-items-center w-100 h-100">
                            { value.lType !== 2 && <MDBIcon icon="map-marker-alt" color="primary" className="me-1" fixed/> }
                            { value.lType == 2 && <MDBIcon icon="gear" color="primary" className="me-1" fixed/> }
                            { value.lType == 1 && <MDBIcon icon="bookmark" color="warning" className="me-1"/> }
                            <div className="d-flex flex-column" style={{lineHeight: value.address?'0.8rem':'1rem'}}>
                                <span className={value.lType == 1?"fst-italic":""}>{value.Name}</span>
                                <span className="text-muted" style={{fontSize: '80%'}}>{value.address}</span>
                            </div>
                        </span>
                    ),
                    comparator: (row1, row2) => DataTableStringComparison(row1.data.Name, row2.data.Name),
                    quickFilterText: data => data.Name + data.address
                },
                { headerName: t('machines'), field: 'nrOfMachines', flex: 1}
            ]}
        />
    ), [locations, search]);

    const LocationEditScreen = useMemo(() => ( selectedId == null ? null :
        <LocationEditor
            locationId={selectedId}
            canEdit={canEdit}
            canDelete={canDelete}
            canAssignMachines={canAssignMachines}
            defaultCountry={userData?.company?.country}
            onClose={() => setSelectedId(null)}
            onAdd={() => { setSelectedId(null); fetch(); }}
            onDelete={() => setDeleteId(selectedId)}
            onEdit={() => fetch()}
        />
    ), [selectedId, canEdit, canDelete, canAssignMachines, userData, fetch]);

    return (
        <AdminPage
            title={t('locations')}
            header={(
                <MDBBtn color="link" onClick={() => setSelectedId(-1)}>
                    <MDBIcon icon="plus-circle" className="me-1"/>
                    {t('add_location')}
                </MDBBtn>
            )}
        >
            <DeleteConfirmModal
                open={deleteId !== null}
                title={t('remove_location')}
                message={t('remove_location_prompt')}
                onCancel={() => setDeleteId(null)}
                onConfirm={() => {
                    API.DELETE(`/api/admlocations/${deleteId}`)
                        .then(() => {
                            if (deleteId == selectedId) setSelectedId(null);
                            fetch();
                        })
                        .catch(err => {
                            alert(err.message);
                        })
                    setDeleteId(null);
                }}
            />
            <SearchInput onSearch={setSearch}/>
            <SplitScreenDiv
                ratio={50}
                left={Table}
                right={LocationEditScreen}
            />
        </AdminPage>
    );
}

